import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider.js";
import { Badge, Button, Card, Modal, Stepper } from "../components/index.js";
import type { AppSettings, PlanSummary } from "../types.js";
import { planBadge } from "../lib/plan-status.js";
import {
  buildCreatePlanInputFromSettings,
  canCancelPlan,
  canDeletePlan,
  canEditPlan,
  isSourceConfigComplete
} from "../lib/plan-actions.js";
import { suggestNextPlanName } from "../lib/plan-name.js";
import { STEP_LABELS } from "./criar/step-props.js";
import { Step1Produtos } from "./criar/Step1Produtos.js";
import { Step2Embalagem } from "./criar/Step2Embalagem.js";
import { Step3Confirmacao } from "./criar/Step3Confirmacao.js";
import { Step4Etiquetas } from "./criar/Step4Etiquetas.js";
import { Step5Rastreamento } from "./criar/Step5Rastreamento.js";
import { CancelarPlanoModal } from "./criar/CancelarPlanoModal.js";

/**
 * Criar envios — wizard de 5 etapas (Design.md §6). Sem ?planId, mostra a lista
 * de planos (PlanPicker) com as ações Criar / Editar / Cancelar / Excluir. "Criar
 * envios" cria um rascunho a partir da origem cadastrada em Configurações (Seller
 * ID + endereço) e abre a Etapa 1 — sem pedir endereço/telefone no wizard. Com
 * ?planId, renderiza o wizard daquele plano.
 */
const LAST_STEP = STEP_LABELS.length - 1;

export function CriarEnviosPage(): ReactNode {
  const [params, setParams] = useSearchParams();
  const planId = params.get("planId");
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  // Etapas 3–5 são finas (sem gating); 1 e 2 controlam o avanço.
  const [ready, setReady] = useState<boolean[]>([
    false,
    false,
    true,
    true,
    true
  ]);

  const updateReady = useCallback((index: number, value: boolean) => {
    setReady((prev) => {
      if (prev[index] === value) {
        return prev;
      }
      const next = [...prev];
      next[index] = value;
      return next;
    });
  }, []);

  if (planId === null) {
    return (
      <PlanPicker onPick={(id) => setParams({ planId: id })} />
    );
  }

  const canAdvance = ready[step] ?? true;

  function advance(): void {
    if (step < LAST_STEP) {
      setStep(step + 1);
    } else {
      navigate("/");
    }
  }

  const primaryLabel =
    step === LAST_STEP ? "Concluir" : step === LAST_STEP - 1 ? "Finalizar envio" : "Avançar";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
      <div>
        <h2 style={{ fontSize: 21, fontWeight: 700 }}>Criar envio</h2>
        <p style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: "0.2rem" }}>
          Fluxo STA · Amazon UK · plano <span className="mono">{planId}</span>
        </p>
      </div>

      <Card>
        <Stepper
          steps={STEP_LABELS}
          current={step}
          onStepClick={(i) => {
            // Só permite voltar/revisitar ou avançar se as etapas anteriores
            // estão prontas (não burla os gates de §6.1/§6.3).
            if (i <= step || ready.slice(0, i).every(Boolean)) {
              setStep(i);
            }
          }}
        />
      </Card>

      <div>{renderStep(step, planId, (r) => updateReady(step, r), setStep)}</div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "0.75rem"
        }}
      >
        {step > 0 ? (
          <Button variant="secondary" onClick={() => setStep(step - 1)}>
            ‹ Voltar
          </Button>
        ) : (
          <span />
        )}
        <span style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
          Etapa {step + 1} de {STEP_LABELS.length}
        </span>
        <Button onClick={advance} disabled={!canAdvance}>
          {primaryLabel}
        </Button>
      </div>
    </div>
  );
}

function renderStep(
  step: number,
  planId: string,
  onReadyChange: (ready: boolean) => void,
  onGoToStep: (step: number) => void
): ReactNode {
  switch (step) {
    case 0:
      return <Step1Produtos planId={planId} onReadyChange={onReadyChange} />;
    case 1:
      return (
        <Step2Embalagem
          planId={planId}
          onReadyChange={onReadyChange}
          onGoToStep={onGoToStep}
        />
      );
    case 2:
      return <Step3Confirmacao planId={planId} onReadyChange={onReadyChange} />;
    case 3:
      return <Step4Etiquetas planId={planId} onReadyChange={onReadyChange} />;
    default:
      return <Step5Rastreamento planId={planId} onReadyChange={onReadyChange} />;
  }
}

function PlanPicker({ onPick }: { onPick: (id: string) => void }): ReactNode {
  const { api, me } = useAuth();
  const navigate = useNavigate();
  const isAdmin = me?.role === "ADMIN";
  const [plans, setPlans] = useState<PlanSummary[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [criando, setCriando] = useState(false);
  const [needsConfig, setNeedsConfig] = useState(false);
  // Modal do NOME do envio (req 1): abre após validar a origem; nome pré-sugerido
  // como FICC-NNN (sequência derivada dos planos existentes).
  const [nomeOpen, setNomeOpen] = useState(false);
  const [nomeEnvio, setNomeEnvio] = useState("");
  const [settingsCache, setSettingsCache] = useState<AppSettings | null>(null);
  const [cancelTarget, setCancelTarget] = useState<PlanSummary | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PlanSummary | null>(null);
  const [excluindo, setExcluindo] = useState(false);

  const load = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const res = await api.listPlans({ limit: 50 });
      setPlans(res.plans);
    } catch (error: unknown) {
      setErro(error instanceof Error ? error.message : "Falha ao carregar planos.");
    } finally {
      setCarregando(false);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  // "Criar envios": valida a origem das Configurações e, se OK, abre o modal do
  // NOME (req 1) com a sugestão FICC-NNN pré-preenchida. Se a origem não está
  // configurada, manda o operador para Configurações (não pede endereço aqui).
  async function abrirCriar(): Promise<void> {
    setErro(null);
    setNeedsConfig(false);
    setCriando(true);
    try {
      const { settings } = await api.getSettings();
      if (!isSourceConfigComplete(settings)) {
        setNeedsConfig(true);
        return;
      }
      setSettingsCache(settings);
      setNomeEnvio(suggestNextPlanName(plans));
      setNomeOpen(true);
    } catch (error: unknown) {
      setErro(error instanceof Error ? error.message : "Falha ao iniciar a criação.");
    } finally {
      setCriando(false);
    }
  }

  // Confirma a criação com o nome informado e abre a Etapa 1 do novo plano.
  async function confirmarCriar(): Promise<void> {
    if (settingsCache === null) {
      return;
    }
    setErro(null);
    setCriando(true);
    try {
      const res = await api.createDraftPlan(
        buildCreatePlanInputFromSettings(settingsCache, nomeEnvio)
      );
      setNomeOpen(false);
      onPick(res.plan.id);
    } catch (error: unknown) {
      setErro(error instanceof Error ? error.message : "Falha ao criar o envio.");
    } finally {
      setCriando(false);
    }
  }

  async function confirmarExclusao(): Promise<void> {
    if (deleteTarget === null) {
      return;
    }
    setExcluindo(true);
    setErro(null);
    try {
      await api.deletePlan(deleteTarget.id);
      setDeleteTarget(null);
      await load();
    } catch (error: unknown) {
      setErro(error instanceof Error ? error.message : "Falha ao excluir o plano.");
    } finally {
      setExcluindo(false);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.1rem" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "0.75rem" }}>
        <div>
          <h2 style={{ fontSize: 21, fontWeight: 700 }}>Criar envio</h2>
          <p style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: "0.2rem" }}>
            Crie um novo envio ou edite/cancele um existente · Amazon UK.
          </p>
        </div>
        {isAdmin ? (
          <Button onClick={() => void abrirCriar()} disabled={criando}>
            {criando ? "Criando…" : "+ Criar envios"}
          </Button>
        ) : null}
      </div>
      {needsConfig ? (
        <Card>
          <p role="alert" style={{ color: "var(--orange)", fontSize: 13, marginBottom: "0.6rem" }}>
            Configure o <strong>Seller ID</strong> e o <strong>endereço de origem</strong>{" "}
            em Configurações antes de criar um envio. Esses dados são usados pela
            Amazon ao registrar o plano.
          </p>
          <Button variant="secondary" onClick={() => navigate("/configuracoes")}>
            Ir para Configurações
          </Button>
        </Card>
      ) : null}
      <Card>
        {erro !== null ? (
          <p role="alert" style={{ color: "var(--orange)" }}>{erro}</p>
        ) : null}
        {carregando ? <p role="status">Carregando planos…</p> : null}
        {!carregando && plans.length === 0 ? (
          <p style={{ color: "var(--text-muted)" }}>
            Nenhum plano encontrado.{" "}
            {isAdmin ? 'Use "Criar envios" para começar.' : ""}
          </p>
        ) : null}
        <div style={{ display: "flex", flexDirection: "column", gap: "0.45rem" }}>
          {plans.map((plan) => {
            const badge = planBadge(plan.status);
            const editavel = canEditPlan(plan);
            const cancelavel = canCancelPlan(plan);
            const excluivel = canDeletePlan(plan);
            return (
              <div
                key={plan.id}
                className="ficc-hover-row"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "0.75rem",
                  background: "var(--panel)",
                  border: "1px solid var(--border-soft)",
                  borderRadius: 9,
                  padding: "0.6rem 0.8rem"
                }}
              >
                <span style={{ minWidth: 0 }}>
                  <span style={{ fontWeight: 600 }}>{plan.name ?? "(sem nome)"}</span>
                  <span className="mono" style={{ fontSize: 11, color: "var(--text-muted)", marginLeft: "0.5rem" }}>
                    {plan.id}
                  </span>
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: "0.5rem", flex: "0 0 auto" }}>
                  <Badge tone={badge.tone}>{badge.label}</Badge>
                  <Button
                    variant={editavel ? "primary" : "secondary"}
                    onClick={() => onPick(plan.id)}
                  >
                    {editavel ? "Editar" : "Visualizar"}
                  </Button>
                  {isAdmin && cancelavel ? (
                    <Button variant="ghost" onClick={() => setCancelTarget(plan)}>
                      Cancelar
                    </Button>
                  ) : null}
                  {isAdmin && excluivel ? (
                    <Button
                      variant="ghost"
                      onClick={() => setDeleteTarget(plan)}
                      style={{ color: "var(--orange)" }}
                    >
                      Excluir
                    </Button>
                  ) : null}
                </span>
              </div>
            );
          })}
        </div>
      </Card>

      <Modal
        open={nomeOpen}
        onClose={() => setNomeOpen(false)}
        title="Criar envio"
        width={460}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setNomeOpen(false)}
              disabled={criando}
            >
              Cancelar
            </Button>
            <Button
              onClick={() => void confirmarCriar()}
              disabled={criando || nomeEnvio.trim().length === 0}
            >
              {criando ? "Criando…" : "Criar envio"}
            </Button>
          </>
        }
      >
        <label style={{ display: "block" }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: "0.3rem" }}>
            Nome do envio <span style={{ color: "var(--orange)" }}>*</span>
          </div>
          <input
            type="text"
            value={nomeEnvio}
            autoFocus
            onChange={(e) => setNomeEnvio(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && nomeEnvio.trim().length > 0 && !criando) {
                void confirmarCriar();
              }
            }}
            placeholder="FICC-001"
            aria-label="Nome do envio"
            style={{ width: "100%" }}
          />
        </label>
        <p style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: "0.5rem" }}>
          Sugerimos a sequência <strong>FICC-NNN</strong>. Use o próximo número da
          sequência para manter os envios organizados.
        </p>
      </Modal>

      <Modal
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        title="Excluir plano de envio"
        width={480}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setDeleteTarget(null)}
              disabled={excluindo}
            >
              Voltar
            </Button>
            <Button
              onClick={() => void confirmarExclusao()}
              disabled={excluindo}
              style={{ background: "var(--orange)" }}
            >
              {excluindo ? "Excluindo…" : "Excluir definitivamente"}
            </Button>
          </>
        }
      >
        <p style={{ fontSize: 13 }}>
          Excluir permanentemente o plano{" "}
          <strong>{deleteTarget?.name ?? deleteTarget?.id ?? ""}</strong>? Esta
          ação não pode ser desfeita. (Só rascunhos não iniciados ou planos
          cancelados/com falha podem ser excluídos — planos ativos usam Cancelar.)
        </p>
      </Modal>
      <CancelarPlanoModal
        open={cancelTarget !== null}
        plan={cancelTarget}
        onClose={() => setCancelTarget(null)}
        onCancelled={() => {
          // NÃO fechar aqui: o modal precisa permanecer aberto para exibir a tela
          // "Cancelamento enfileirado" (o cancelamento é assíncrono — o status só
          // vira CANCELADO depois). O botão "Fechar" do modal (onClose) é quem
          // limpa o cancelTarget. Aqui só recarregamos a lista por baixo.
          void load();
        }}
      />
    </div>
  );
}
